import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, apiError, API } from "@/lib/api";
import { AdminNav } from "@/components/AdminNav";
import {
  ArrowLeft, AlertTriangle, Clock, Phone, MapPin, Wallet, FileText, Globe,
  CalendarClock, Send, StickyNote, Lock, Flag, Reply,
} from "lucide-react";
import { toast } from "sonner";

function fmt(d) { return new Date(d).toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" }); }

export default function AdminJob() {
  const nav = useNavigate();
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [propDate, setPropDate] = useState("");
  const [propSlot, setPropSlot] = useState("");
  const token = localStorage.getItem("mrshu_admin_token");
  const endRef = useRef(null);

  const loadJob = useCallback(async () => {
    try { const { data } = await adminApi.get(`/admin/quotes/${id}`); setQ(data); }
    catch (e) { if ([401, 403].includes(e?.response?.status)) nav("/admin/login"); }
  }, [id, nav]);
  const loadMessages = useCallback(async () => {
    try { const { data } = await adminApi.get(`/admin/quotes/${id}/messages`); setMessages(data); } catch {}
  }, [id]);

  useEffect(() => {
    if (!token) { nav("/admin/login"); return; }
    adminApi.get("/meta/options").then(({ data }) => { setStatuses(data.statuses); setApptTypes(data.appointment_types); setPeriods(data.time_periods); }).catch(() => {});
    loadJob(); loadMessages();
  }, [loadJob, loadMessages, nav, token]);
  useEffect(() => { const iv = setInterval(() => { loadMessages(); loadJob(); }, 6000); return () => clearInterval(iv); }, [loadMessages, loadJob]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const changeStatus = async (status) => {
    await adminApi.patch(`/admin/quotes/${id}/status`, { status });
    toast.success(`Status → ${status}`); loadJob();
  };
  const send = async () => {
    if (!text.trim()) return;
    await adminApi.post(`/admin/quotes/${id}/messages`, { text }); setText(""); loadMessages(); loadJob();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await adminApi.post(`/admin/quotes/${id}/notes`, { text: note }); setNote(""); loadJob(); toast.success("Note added");
  };
  const toggleFollowUp = async () => {
    await adminApi.post(`/admin/quotes/${id}/follow-up`, { value: !q.follow_up }); loadJob();
    toast.success(!q.follow_up ? "Marked for follow up" : "Follow up removed");
  };
  const noReplyNeeded = async () => {
    await adminApi.post(`/admin/quotes/${id}/no-reply-needed`); loadJob(); toast.success("Marked: no reply needed");
  };
  const appt = async (action, extra = {}) => {
    try {
      const { data } = await adminApi.post(`/admin/quotes/${id}/appointment`, { action, ...extra });
      if (data.conflict) {
        const c = data.conflicts.map((x) => `${x.ref_number} (${x.customer}) ${x.date} ${x.slot}`).join("\n");
        if (window.confirm(`Scheduling conflict with:\n${c}\n\nConfirm anyway?`)) {
          await adminApi.post(`/admin/quotes/${id}/appointment`, { action, ...extra, acknowledge_conflict: true });
          toast.success("Confirmed (overlap acknowledged)"); loadJob();
        }
        return;
      }
      toast.success("Appointment updated"); loadJob();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!q) return (<div className="min-h-screen bg-background"><AdminNav active="requests" /><p className="text-center py-20 text-muted-foreground">Loading…</p></div>);
  const urgent = q.urgency === "Urgent";
  const a = q.appointment;

  return (
    <div className="min-h-screen bg-background">
      <AdminNav active="requests" />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <button data-testid="adminjob-back-btn" onClick={() => nav("/admin")} className="flex items-center gap-2 text-primary font-medium mb-6"><ArrowLeft className="h-5 w-5" /> Requests</button>

        <div className="flex items-center justify-between gap-2">
          <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${urgent ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"}`}>
            {urgent ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />} {q.urgency}
          </span>
          <span data-testid="adminjob-ref" className="font-display font-bold text-primary">{q.ref_number}</span>
        </div>
        <h2 className="font-display text-2xl font-bold text-primary mt-2">{q.name} · {q.work_type}</h2>

        {q.status === "customer_cancelled" && (
          <div data-testid="adminjob-cancelled-banner" className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <p className="font-bold">Cancelled by customer · 客户主动取消</p>
            <p>Cancelled: {q.cancelled_at ? fmt(q.cancelled_at) : "—"}</p>
            {q.cancellation_reason && <p>Reason: {q.cancellation_reason}</p>}
            {q.previous_status && <p>Previous status: {q.previous_status}</p>}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button data-testid="followup-toggle-btn" onClick={toggleFollowUp}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${q.follow_up ? "bg-red-600 text-white" : "border border-red-400 text-red-600 hover:bg-red-50"}`}>
            <Flag className="h-4 w-4" /> {q.follow_up ? "Remove Follow Up" : "Mark for Follow Up"}
          </button>
          {q.awaiting_reply && (
            <>
              <span data-testid="adminjob-awaiting" className="flex items-center gap-1 rounded-full bg-amber-500 px-3 py-2 text-sm font-bold text-white"><Reply className="h-4 w-4" /> Awaiting reply</span>
              <button data-testid="no-reply-needed-btn" onClick={noReplyNeeded}
                className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary">No Reply Needed</button>
            </>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-5">
          <div className="rounded-3xl border border-border bg-white p-5 shadow-sm space-y-2 text-sm">
            <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary/70" /> <a href={`tel:${q.phone}`} className="hover:underline">{q.phone}</a></p>
            <p className="flex items-start gap-2"><MapPin className="h-4 w-4 text-primary/70 mt-0.5" /> {q.address}</p>
            <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary/70" /> {q.preferred_time}</p>
            {q.budget && <p className="flex items-center gap-2"><Wallet className="h-4 w-4 text-primary/70" /> {q.budget}</p>}
            {q.description && <p className="flex items-start gap-2"><FileText className="h-4 w-4 text-primary/70 mt-0.5" /> {q.description}</p>}
            {q.vehicle && (
              <div data-testid="adminjob-vehicle" className="rounded-xl border border-primary/20 bg-secondary/40 p-3 text-sm space-y-0.5">
                <p className="font-bold text-primary">Vehicle · {q.vehicle.plate} <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs">Mobile Service</span></p>
                <p>Fuel: {q.vehicle.fuel_type}{q.vehicle.odometer_km ? ` · ${q.vehicle.odometer_km} km` : ""}</p>
                {q.vehicle.make_model_year && <p>{q.vehicle.make_model_year}</p>}
                <p>Service location: {q.address}</p>
                {q.vehicle.parking_notes && <p>Access: {q.vehicle.parking_notes}</p>}
                <p>Spark-plug quote requested: {q.vehicle.spark_plug_quote ? "Yes" : "No"}</p>
              </div>
            )}
            <p className="flex items-center gap-2"><Globe className="h-4 w-4 text-primary/70" /> Language: {q.language_label}</p>
            <p className="text-xs text-muted-foreground pt-1">Submitted {fmt(q.created_at)} · Updated {fmt(q.updated_at)}</p>
            {q.media?.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {q.media.map((m) => m.kind === "video" ? (
                  <a key={m.id} href={`${API}/files/${m.id}?auth=${token}`} target="_blank" rel="noreferrer" className="flex h-16 w-16 items-center justify-center rounded-lg bg-primary text-primary-foreground text-xs">Video</a>
                ) : (
                  <a key={m.id} href={`${API}/files/${m.id}?auth=${token}`} target="_blank" rel="noreferrer"><img src={`${API}/files/${m.id}?auth=${token}`} alt={m.filename} className="h-16 w-16 rounded-lg object-cover border border-border" /></a>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
            <p className="font-semibold text-primary mb-2">Status</p>
            <select data-testid="adminjob-status-select" value={q.status} onChange={(e) => changeStatus(e.target.value)}
              className="w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm font-medium focus:ring-2 focus:ring-primary focus:outline-none">
              {!statuses.includes(q.status) && <option value={q.status}>Customer Cancelled</option>}
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <p className="font-semibold text-primary mt-4 mb-2">Status history</p>
            <ol className="space-y-1.5 text-sm max-h-40 overflow-auto" data-testid="adminjob-history">
              {q.status_history?.map((h, i) => (
                <li key={i} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary" /> {h.status} <span className="text-xs text-muted-foreground">· {fmt(h.at)} · {h.by === "customer" ? "Customer" : "MR SHU Handyman"}</span></li>
              ))}
            </ol>
          </div>
        </div>

        {/* Appointment */}
        <div className="mt-6 rounded-3xl border border-border bg-white p-5 shadow-sm" data-testid="adminjob-appointment">
          <p className="flex items-center gap-2 font-display font-bold text-primary"><CalendarClock className="h-5 w-5" /> Appointment</p>
          {q.visit_preference && <p className="text-sm mt-1">Customer preference: <span className="font-medium">{q.visit_preference}</span></p>}
          {q.preferred_times?.length > 0 && (
            <div className="mt-2 space-y-1 text-sm">
              <p className="font-medium">Customer preferred times:</p>
              {q.preferred_times.map((pt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span>{pt.date || "—"} · {pt.period || "—"} {pt.note ? `· ${pt.note}` : ""}</span>
                  <button data-testid={`accept-time-${i}`} onClick={() => appt("accept_time", { time_index: i })} className="text-xs rounded-full bg-primary text-primary-foreground px-2 py-1">Accept & confirm</button>
                </div>
              ))}
            </div>
          )}
          {a && <p className="text-sm mt-2">Current: <span className="font-semibold">{a.type} · {a.state}{a.date ? ` · ${a.date} ${a.slot || ""}` : ""}</span></p>}
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <input type="date" data-testid="propose-date" value={propDate} onChange={(e) => setPropDate(e.target.value)} className="h-11 rounded-lg border border-input px-3 text-sm" />
            <select data-testid="propose-slot" value={propSlot} onChange={(e) => setPropSlot(e.target.value)} className="h-11 rounded-lg border border-input bg-white px-3 text-sm">
              <option value="">Slot</option>{periods.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <button data-testid="propose-btn" onClick={() => appt("propose", { date: propDate, slot: propSlot })} className="h-11 rounded-full bg-secondary text-secondary-foreground px-4 text-sm font-semibold">Propose</button>
            <button data-testid="confirm-btn" onClick={() => appt("confirm", { date: propDate || a?.date, slot: propSlot || a?.slot })} className="h-11 rounded-full bg-primary text-primary-foreground px-4 text-sm font-semibold">Confirm</button>
            <button data-testid="reschedule-btn" onClick={() => appt("reschedule", { date: propDate, slot: propSlot })} className="h-11 rounded-full border border-border px-4 text-sm font-semibold">Reschedule</button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {apptTypes.map((tp) => <button key={tp} data-testid={`change-type-${tp}`} onClick={() => appt("change_type", { appt_type: tp })} className="rounded-full border border-border px-3 py-1.5 text-xs">Set: {tp}</button>)}
            <button data-testid="complete-appt-btn" onClick={() => appt("complete")} className="rounded-full border border-border px-3 py-1.5 text-xs">Mark completed</button>
            <button data-testid="cancel-appt-btn" onClick={() => appt("cancel")} className="rounded-full border border-destructive text-destructive px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </div>

        {/* Conversation + internal notes */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
            <p className="font-display font-bold text-primary mb-2">Conversation</p>
            <div className="space-y-3 max-h-72 overflow-y-auto" data-testid="adminjob-messages">
              {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No messages yet.</p>}
              {messages.map((m) => {
                const admin = m.sender === "admin";
                return (
                  <div key={m.id} className={`flex ${admin ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${admin ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                      <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                      <p className={`text-[10px] mt-1 ${admin ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{admin ? "MR SHU Handyman" : q.name} · {fmt(m.created_at)}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input data-testid="adminjob-message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Reply to customer…" className="flex-1 h-12 rounded-full border border-input px-4 text-sm focus:ring-2 focus:ring-primary focus:outline-none" />
              <button data-testid="adminjob-message-send" onClick={send} className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground"><Send className="h-5 w-5" /></button>
            </div>
          </div>

          <div id="notes" className="rounded-3xl border border-amber-300 bg-amber-50 p-5 shadow-sm">
            <p className="flex items-center gap-2 font-display font-bold text-amber-800"><Lock className="h-4 w-4" /> Internal notes <span className="text-xs font-normal">(never shown to customer)</span></p>
            <div className="mt-2 space-y-2 max-h-56 overflow-auto" data-testid="adminjob-notes">
              {(!q.admin_notes || q.admin_notes.length === 0) && <p className="text-sm text-amber-700/70">No internal notes.</p>}
              {q.admin_notes?.map((n) => (
                <div key={n.id} className="rounded-xl bg-white/70 p-3 text-sm">
                  <p className="whitespace-pre-wrap">{n.text}</p>
                  <p className="text-[10px] text-amber-700/70 mt-1">{fmt(n.at)}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input data-testid="adminjob-note-input" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add internal note…" className="flex-1 h-12 rounded-full border border-amber-300 px-4 text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none" />
              <button data-testid="adminjob-note-add" onClick={addNote} className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-600 text-white"><StickyNote className="h-5 w-5" /></button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
